import { useState, useMemo, useEffect } from 'react'
import { format } from 'date-fns'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Info, Copy, Check } from 'lucide-react'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDate, formatPower } from '@/lib/utils'
import { InfoTip } from '@/components/ui/info-tip'
import type { OptimizationResult, TradingDeskResult } from '@/lib/ev-optimizer'
import { SCENARIO_KEYS, SCENARIO_LABELS, SCENARIO_COLORS, type ScenarioKey } from '@/lib/simulator-scenarios'
import type { components } from '../../../../../types/flex-api'

type ChargingSession = components['schemas']['ChargingSession']

export interface SessionRow {
  sessionId: string
  pluggedAt: string
  durationMin: number
  energyKwh: number
  flatCost: number
  optimalCost: number
  savings: number
  savingsPct: number
  result: OptimizationResult
  raw: ChargingSession
  // Trading Desk perspective: baseline (immediate charge) vs optimized, using DA market prices
  tdDA?: TradingDeskResult
  // Trading Desk "Optimized" scenarios (DA / IDCFull / Cross-Market)
  tdScenarios?: Partial<Record<ScenarioKey, TradingDeskResult>>
  // Per-series IDA optimization results (optional — only present when IDA prices are available)
  idaResults?: Record<string, { optimalCost: number; savings: number; savingsPct: number }>
}

type SortKey = 'pluggedAt' | 'durationMin' | 'energyKwh' | 'savings' | 'savingsPct'

interface Props {
  rows: SessionRow[]
  flatRate: number
  offset: number
  selectedRows: string[]
  onRowSelect: (row: SessionRow) => void
  idcFullKey?: string
}

function SessionDetailDialog({
  session,
  durationMin,
  onClose,
}: {
  session: ChargingSession
  durationMin: number
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  function copyId() {
    navigator.clipboard.writeText(session.id)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const fields: { label: string; value: string }[] = [
    { label: 'Plugged at', value: formatDate(session.pluggedAt) },
    { label: 'Unplugged at', value: formatDate(session.unpluggedAt) },
    {
      label: 'Scheduled departure',
      value: session.scheduledDepartureTime ? formatDate(session.scheduledDepartureTime) : '—',
    },
    {
      label: 'Duration',
      value:
        durationMin >= 60
          ? `${Math.floor(durationMin / 60)}h ${durationMin % 60}m`
          : `${durationMin}m`,
    },
    { label: 'Start SoC', value: `${session.pluggedSoC}%` },
    {
      label: 'Target SoC',
      value: session.desiredSoC !== undefined ? `${session.desiredSoC}%` : '—',
    },
    { label: 'Final SoC', value: `${session.unpluggedSoC}%` },
    {
      label: 'Charge mode',
      value: session.chargeMode === 'FORCED_EV' ? 'Forced EV' : 'Scheduled',
    },
    {
      label: 'Battery capacity',
      value: `${(session.maxEnergy / 1000).toFixed(1)} kWh`,
    },
    { label: 'Max charge power', value: formatPower(session.maxChargePower) },
    { label: 'Median charge power', value: formatPower(session.medianChargePower) },
  ]

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Session Details</DialogTitle>
        </DialogHeader>
        <TooltipProvider>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            {/* Session ID — truncated with tooltip + copy */}
            <dt className="text-muted-foreground self-center">Session ID</dt>
            <dd className="flex items-center justify-end gap-1.5 font-medium min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="truncate font-mono text-xs cursor-default">
                    {session.id.slice(0, 8)}…
                  </span>
                </TooltipTrigger>
                <TooltipContent side="left" className="font-mono text-xs max-w-xs break-all">
                  {session.id}
                </TooltipContent>
              </Tooltip>
              <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0" onClick={copyId}>
                {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
              </Button>
            </dd>

            {fields.map(({ label, value }) => (
              <div key={label} className="contents">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-medium text-right">{value}</dd>
              </div>
            ))}
          </dl>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  )
}

const PAGE_SIZE = 100

export function SessionBreakdownTable({ rows, flatRate: _flatRate, offset: _offset, selectedRows, onRowSelect, idcFullKey }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('pluggedAt')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [detailRow, setDetailRow] = useState<SessionRow | null>(null)
  const [page, setPage] = useState(0)

  useEffect(() => {
    setPage(0)
  }, [rows, sortKey, sortDir])

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sorted = [...rows].sort((a, b) => {
    const va = a[sortKey]
    const vb = b[sortKey]
    const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number)
    return sortDir === 'asc' ? cmp : -cmp
  })

  function SortIcon({ k }: { k: SortKey }) {
    if (sortKey !== k) return <span className="ml-1 opacity-30">↕</span>
    return sortDir === 'asc' ? (
      <ChevronUp className="inline h-3 w-3 ml-1" />
    ) : (
      <ChevronDown className="inline h-3 w-3 ml-1" />
    )
  }

  function savingsBadgeVariant(pct: number): 'default' | 'secondary' | 'outline' {
    if (pct >= 20) return 'default'
    if (pct >= 5) return 'secondary'
    return 'outline'
  }

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const paginated = useMemo(
    () => sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [sorted, page]
  )

  const idaKeys = useMemo(() => {
    const keys = new Set<string>()
    rows.forEach((r) => r.idaResults && Object.keys(r.idaResults).forEach((k) => keys.add(k)))
    if (idcFullKey) keys.delete(idcFullKey)
    return Array.from(keys).sort()
  }, [rows, idcFullKey])

  const scenarioKeys = useMemo(
    () => SCENARIO_KEYS.filter((k) => rows.some((r) => r.tdScenarios?.[k] !== undefined)),
    [rows]
  )

  const hasTD = rows.some((r) => r.tdDA !== undefined)

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border py-12 text-center text-sm text-muted-foreground">
        No sessions to display for the selected range.
      </div>
    )
  }

  return (
    <>
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader className="bg-muted/80">
              <TableRow>
                <TableHead
                  className="cursor-pointer select-none whitespace-nowrap"
                  onClick={() => handleSort('pluggedAt')}
                >
                  Date <SortIcon k="pluggedAt" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort('durationMin')}
                >
                  Duration <SortIcon k="durationMin" />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort('energyKwh')}
                >
                  Energy <SortIcon k="energyKwh" />
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  <span className="inline-flex items-center gap-1 justify-end">
                    Flat Cost
                    <InfoTip content="Hypothetical cost if the full session energy were charged at the configured flat rate + grid offset." side="top" />
                  </span>
                </TableHead>
                <TableHead className="text-right whitespace-nowrap">
                  <span className="inline-flex items-center gap-1 justify-end">
                    Optimal
                    <InfoTip content="Simulated cost: energy shifted into the cheapest DA price slots within the session's plug-in window, at market price + grid offset." side="top" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right whitespace-nowrap"
                  onClick={() => handleSort('savings')}
                >
                  <span className="inline-flex items-center gap-1 justify-end">
                    Savings <SortIcon k="savings" />
                    <InfoTip content="Flat cost − optimal cost. How much the end-customer saves vs. paying a fixed tariff." side="top" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none text-right"
                  onClick={() => handleSort('savingsPct')}
                >
                  % <SortIcon k="savingsPct" />
                </TableHead>
                {hasTD && (
                  <>
                    <TableHead className="text-right text-amber-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 justify-end">
                        TD Baseline
                        <InfoTip content="Trading Desk baseline: cost if the session charged immediately after plug-in (chronological slot fill), at raw market price only." side="top" />
                      </span>
                    </TableHead>
                    <TableHead className="text-right text-amber-600 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 justify-end">
                        TD Savings
                        <InfoTip content="TD baseline − optimal cost. How much the trading desk saves by optimising vs. charging immediately." side="top" />
                      </span>
                    </TableHead>
                  </>
                )}
                {scenarioKeys.map((k) => (
                  <TableHead key={k} className="text-right whitespace-nowrap" style={{ color: SCENARIO_COLORS[k] }}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {SCENARIO_LABELS[k]} %
                      <InfoTip content={`Trading Desk savings %: ${SCENARIO_LABELS[k]} optimised cost vs. the shared DA immediate-charge baseline.`} side="top" />
                    </span>
                  </TableHead>
                ))}
                {idaKeys.map((k) => (
                  <TableHead key={k} className="text-right text-violet-600 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1 justify-end">
                      {k} %
                      <InfoTip content={`End-customer savings % if this session were optimised against the ${k} intraday price series instead of Day-Ahead.`} side="top" />
                    </span>
                  </TableHead>
                ))}
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map((row) => {
                const isSelected = selectedRows.includes(row.sessionId)
                return (
                  <TableRow
                    key={row.sessionId}
                    className={`cursor-pointer transition-colors ${isSelected ? 'bg-primary/8 hover:bg-primary/12' : ''}`}
                    onClick={() => onRowSelect(row)}
                  >
                    <TableCell className="whitespace-nowrap text-xs">
                      {format(new Date(row.pluggedAt), 'dd MMM HH:mm')}
                      {isSelected && (
                        <span className="ml-1.5 text-[10px] text-primary font-medium">▲ chart</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {row.durationMin >= 60
                        ? `${Math.floor(row.durationMin / 60)}h ${row.durationMin % 60}m`
                        : `${row.durationMin}m`}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {row.energyKwh.toFixed(2)} kWh
                    </TableCell>
                    <TableCell className="text-right text-xs">€{row.flatCost.toFixed(3)}</TableCell>
                    <TableCell className="text-right text-xs">€{row.optimalCost.toFixed(3)}</TableCell>
                    <TableCell className="text-right text-xs font-medium text-green-600">
                      +€{row.savings.toFixed(3)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant={savingsBadgeVariant(row.savingsPct)} className="text-xs">
                        {row.savingsPct.toFixed(1)}%
                      </Badge>
                    </TableCell>
                    {hasTD && (
                      <>
                        <TableCell className="text-right text-xs text-amber-700 dark:text-amber-400">
                          {row.tdDA ? `€${row.tdDA.baselineCost.toFixed(3)}` : '—'}
                        </TableCell>
                        <TableCell className="text-right text-xs font-medium text-amber-700 dark:text-amber-400">
                          {row.tdDA ? `+€${row.tdDA.tdSavings.toFixed(3)}` : '—'}
                        </TableCell>
                      </>
                    )}
                    {scenarioKeys.map((k) => (
                      <TableCell key={k} className="text-right">
                        {row.tdScenarios?.[k] !== undefined ? (
                          <Badge variant={savingsBadgeVariant(row.tdScenarios[k]!.tdSavingsPct)} className="text-xs">
                            {row.tdScenarios[k]!.tdSavingsPct.toFixed(1)}%
                          </Badge>
                        ) : '—'}
                      </TableCell>
                    ))}
                    {idaKeys.map((k) => (
                      <TableCell key={k} className="text-right">
                        {row.idaResults?.[k] !== undefined ? (
                          <Badge variant={savingsBadgeVariant(row.idaResults[k].savingsPct)} className="text-xs">
                            {row.idaResults[k].savingsPct.toFixed(1)}%
                          </Badge>
                        ) : '—'}
                      </TableCell>
                    ))}
                    <TableCell className="text-right p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDetailRow(row)
                        }}
                      >
                        <Info className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-2 border-t bg-muted/30 text-xs text-muted-foreground">
            <span>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, sorted.length)} of {sorted.length} sessions
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-6 w-6 p-0"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="tabular-nums px-1">{page + 1}/{totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                className="h-6 w-6 p-0"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {detailRow && (
        <SessionDetailDialog
          session={detailRow.raw}
          durationMin={detailRow.durationMin}
          onClose={() => setDetailRow(null)}
        />
      )}
    </>
  )
}
